import type { CellClassParams, ColDef, ValueGetterParams } from 'ag-grid-community'

import { titleCase } from '../../../../../utils/general'
import { defaultDateDef } from '../../../../_SiteWide/CellTemplates/typeColumnDefs'
import { MultipleProductGroupCellRenderer, StatusCellRenderer } from '../../../shared/LineItems/components'
import type { InvoiceTransaction, TransactionRow } from '../../../../../api/useFreight/types'
import {
  BOLNumbersRenderer,
  CarrierRenderer,
  CustomerRenderer,
  OrderActionsRenderer,
  OrderNumberRenderer,
} from '../renderers'
import { formatCurrency, formatWithUOM } from '../formatters'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { TransactionErrorCellRenderer } from '../renderers/OrderErrorCellRenderer'
import { Horizontal } from '@gravitate-js/excalibrr'
import { Tag, Tooltip } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { IconUsers } from '@tabler/icons-react'
import { customFLIFilters } from '../filters'

/**
 * Column definitions for the By Order View grid
 * Used in ByOrderView.tsx
 * Shows orders as parent rows with line items nested underneath
 * Provides a hierarchical view of orders and their associated line items
 */
export const orderColumnDefs: ColDef<TransactionRow | InvoiceTransaction>[] = [
  {
    headerName: 'ORDER',
    field: 'order_number',
    flex: 3,
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      innerRenderer: OrderNumberRenderer,
    },
    enableRowGroup: true,
    minWidth: 168,
    width: 168,
    ...customFLIFilters,
  },
  {
    headerName: 'PURCHASE ORDER',
    field: 'po',
  },
  {
    headerName: 'INVOICE #',
    field: 'invoice_number',
    flex: 2,
    valueFormatter: (params) => params.value || 'N/A',
    minWidth: 120,
    width: 120,
  },
  {
    headerName: 'STATUS',
    field: 'status',
    valueFormatter: (params) => titleCase(params.value),
    flex: 1,
    cellRenderer: StatusCellRenderer,
  },
  {
    headerName: 'ERROR',
    field: 'error.type',
    cellRenderer: TransactionErrorCellRenderer,
  },
  {
    headerName: 'ERROR MESSAGE',
    field: 'error.message',
    hide: true,
    suppressColumnsToolPanel: true, // added only for downloading purposes
  },
  {
    headerName: 'FREIGHT AS OF',
    ...defaultDateDef('date'),
    valueFormatter: (params) => (params.value ? formatters.SHORT_DATE(params.value) : '-'),
    initialSort: 'desc',
    minWidth: 180,
    width: 180,
  },
  {
    headerName: 'CUSTOMER',
    field: 'customer_name',
    flex: 3,
    enableRowGroup: true,
    cellRenderer: CustomerRenderer,
  },
  {
    headerName: 'CARRIER',
    field: 'carrier_name',
    enableRowGroup: true,
    minWidth: 140,
    width: 140,
    cellRenderer: CarrierRenderer,
  },
  {
    headerName: 'DESTINATIONS',
    field: 'destinations',
    flex: 2,
    enableRowGroup: true,
  },
  {
    headerName: 'ORIGINS',
    field: 'origins',
    flex: 2,
    valueGetter: (params: ValueGetterParams<TransactionRow | InvoiceTransaction>) => params.data?.origins || [],
    valueFormatter: (params) => params.value?.join(', ') || '',
  },
  {
    headerName: 'PRODUCT GROUPS',
    field: 'product_groups',
    minWidth: 140,
    width: 140,
    cellRenderer: MultipleProductGroupCellRenderer,
  },
  {
    headerName: 'BOL NUMBERS',
    field: 'bol_numbers',
    flex: 2,
    valueGetter: (params: ValueGetterParams<TransactionRow | InvoiceTransaction>) => params.data?.bol_numbers || [],
    cellRenderer: BOLNumbersRenderer,
  },
  {
    headerName: 'DISTANCE',
    field: 'distance',
    valueFormatter: (params) => {
      if (!params.data) return ''
      return formatWithUOM(params.value, params.data.distance_uom, 2)
    },
    cellStyle: { textAlign: 'right' },
    flex: 2,
    filter: 'agNumberColumnFilter',
  },
  {
    headerName: 'VOLUME',
    field: 'volume',
    flex: 2,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) => {
      if (!params.data) return ''
      return formatWithUOM(params.value, params.data.volume_uom, 0)
    },
  },
  {
    headerName: 'NET VOLUME',
    field: 'net_volume',
    flex: 2,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) => {
      if (!params.value || params.value === null) return ''
      return formatWithUOM(params.value, params.data?.volume_uom || '', 0)
    },
    initialHide: true,
  },
  {
    headerName: 'GROSS VOLUME',
    field: 'gross_volume',
    flex: 2,
    type: 'numericColumn',
    valueFormatter: (params) => {
      if (!params.value || params.value === null) return ''
      return formatWithUOM(params.value, params.data?.volume_uom || '', 0)
    },
    initialHide: true,
  },
  {
    headerName: 'BASE',
    field: 'base_total',
    flex: 2,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) => formatCurrency(params.value),
  },
  {
    headerName: 'ACCESSORIAL TOTAL',
    field: 'accessorial_total',
    flex: 2,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) => formatCurrency(params.value),
  },
  {
    headerName: 'SURCHARGE',
    field: 'surcharge_total',
    flex: 2,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) => formatCurrency(params.value),
  },
  {
    headerName: 'TOTAL',
    field: 'total',
    pinned: 'right',
    type: 'numericColumn',
    width: 120,
    flex: 0,
    filter: 'agNumberColumnFilter',
    cellStyle: { fontWeight: 'bold' },
    valueFormatter: (params) => formatCurrency(params.value),
  },
  {
    headerName: '',
    field: 'order_number',
    width: 120,
    flex: 0,
    filter: false,
    pinned: 'right',
    cellStyle: { display: 'flex', alignItems: 'center' },
    valueFormatter: () => '',
    cellRenderer: OrderActionsRenderer,
  },
]
